import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { currentLists, nextStep, roundsToKeep, todayInZurich } from "@vocifuchs/shared";
import { api, noAnswer } from "../lib/api.js";
import { keepRounds, keepStart, keptStart } from "../lib/offline.js";
import { common } from "../copy/common.js";
import { lists as listCopy } from "../copy/lists.js";
import { Fox } from "../components/Fox.js";
import { ListGrid, type ListSummary } from "../components/ListGrid.js";
import { Button, Card } from "../components/ui.js";
import { useSession } from "../session.js";


const LANG = common.lang;

interface Today {
  due_by_lang: { en: number; fr: number };
  streak: number;
  class_goal: { week: string; done: number; target: number } | null;
}

/** Swiss thousands separator: 1’240. */
const count = new Intl.NumberFormat("de-CH");

const SEEN_KEY = "vf.classListsSeenAt";

/** What Start shows, kept on the device to open without a connection (issue #120). */
interface StartData {
  lists: ListSummary[];
  archived: number;
  classLists: ListSummary[];
  today: Today;
  /** The day it was loaded: offline, the big button picks as it did then. */
  day: string;
}

export function HomeScreen() {
  const { user, membership, offline } = useSession();
  const userId = user?.id;
  const navigate = useNavigate();
  const [lists, setLists] = useState<ListSummary[] | null>(null);
  const [archived, setArchived] = useState(0);
  const [classLists, setClassLists] = useState<ListSummary[] | null>(null);
  const [today, setToday] = useState<Today | null>(null);
  const [day, setDay] = useState(todayInZurich);
  const [seenAt] = useState(() => {
    const stored = localStorage.getItem(SEEN_KEY);
    return stored ? Number(stored) : Math.floor(Date.now() / 1000);
  });

  useEffect(() => {
    if (!userId) return;
    let live = true;
    const fromClass = membership
      ? api<{ lists: ListSummary[] }>("/api/lists?scope=class").then((data) => data.lists)
      : Promise.resolve([]);
    void Promise.allSettled([
      api<{ lists: ListSummary[]; archived_count: number }>("/api/lists"),
      api<{ progress: Today }>("/api/me"),
      fromClass,
    ]).then(([mine, me, classData]) => {
      if (!live) return;
      if (mine.status === "fulfilled" && me.status === "fulfilled" && classData.status === "fulfilled") {
        const start: StartData = {
          lists: mine.value.lists,
          archived: mine.value.archived_count,
          classLists: classData.value,
          today: me.value.progress,
          day: todayInZurich(),
        };
        show(start);
        if (membership) {
          try {
            localStorage.setItem(SEEN_KEY, String(Math.floor(Date.now() / 1000)));
          } catch {
            // Storage refused: every class list stays «neu», nothing worse.
          }
        }
        keepStart(userId, start);
        // While there is a connection, keep the rounds Start offers for
        // practising without one (issue #120).
        void keepRounds(userId, roundsToKeep(nextStep(start.lists, start.classLists, start.today.due_by_lang, start.day)));
        return;
      }
      // Without a usable answer: Start as it was last shown.
      const failed = [mine, me, classData].some((r) => r.status === "rejected" && noAnswer(r.reason));
      const kept = failed ? keptStart<StartData>(userId) : null;
      if (kept) {
        show(kept);
        return;
      }
      // Otherwise what did load, as before: one failing source hides nothing else.
      if (mine.status === "fulfilled") {
        setLists(mine.value.lists);
        setArchived(mine.value.archived_count);
      }
      if (me.status === "fulfilled") setToday(me.value.progress);
      if (classData.status === "fulfilled") setClassLists(classData.value);
    });
    return () => {
      live = false;
    };
    // `offline` too: back online, Start loads for real again.
  }, [userId, membership?.id, offline]);

  const show = (start: StartData) => {
    setDay(start.day);
    setLists(start.lists);
    setArchived(start.archived);
    setClassLists(start.classLists);
    setToday(start.today);
  };

  // Wait for every source, so the button does not change its mind once the
  // class lists arrive.
  const next = lists && classLists && today ? nextStep(lists, classLists, today.due_by_lang, day) : null;
  const streak = today?.streak ?? 0;
  // «Aktuell» on top; the groups below show the rest (issue #99).
  const current = lists && classLists ? currentLists(lists, classLists) : [];
  const notCurrent = (group: ListSummary[] | null) => group?.filter((list) => list.mark !== "current") ?? null;

  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="flex items-center gap-4">
        <Fox mood="happy" className="h-14 w-14 shrink-0 sm:h-16 sm:w-16" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink-soft">{listCopy.home.hello}</p>
          <h1 className="truncate text-3xl sm:text-4xl">{user?.display_name}</h1>
        </div>
        {/* A number, nothing more: no flame that goes out, no warning (docs/UX.md §9). */}
        {streak >= 1 ? (
          <p
            className="shrink-0 rounded-full border border-line bg-paper-3 px-3 py-1.5 text-sm font-semibold"
            aria-label={listCopy.home.streakAria(streak)}
          >
            <span className="font-display text-lg tabular-nums">{streak}</span>{" "}
            <span className="text-ink-soft">{listCopy.home.streakUnit(streak)}</span>
          </p>
        ) : null}
      </header>

      <Card className="p-5 sm:p-7 lg:flex lg:items-center lg:justify-between lg:gap-8">
        <div>
          <h2 className="text-2xl sm:text-3xl">
            {next?.kind === "due" ? listCopy.home.dueTitle : listCopy.home.continue}
          </h2>
          <p className="mt-1 text-ink-soft">
            {next === null
              ? common.loading
              : next.kind === "list"
                ? `${next.list.title} · ${common.words(next.list.item_count)}`
                : next.kind === "due"
                  ? next.langs.map(({ lang, n }) => listCopy.home.dueWords(n, LANG[lang])).join(" · ")
                  : listCopy.home.emptyContinue}
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-3 lg:mt-0 lg:w-56 lg:shrink-0">
          {next?.kind === "due" ? (
            // One language per round; with both due, the bigger pile first.
            next.langs.map(({ lang }, i) => (
              <Button
                key={lang}
                variant={i === 0 ? "primary" : "ghost"}
                onClick={() => navigate(`/practice/due/${lang}`)}
                className="text-lg"
              >
                {next.langs.length > 1 ? `${listCopy.home.practice}: ${LANG[lang]}` : listCopy.home.practice}
              </Button>
            ))
          ) : (
            <Button
              disabled={next?.kind !== "list"}
              onClick={() => next?.kind === "list" && navigate(`/practice/${next.list.id}`)}
              className="text-lg"
            >
              {listCopy.home.practice}
            </Button>
          )}
        </div>
      </Card>

      {today?.class_goal ? <ClassGoal {...today.class_goal} /> : null}

      {current.length > 0 ? (
        <ListGrid kind="current" title={listCopy.home.current} lists={current} seenAt={seenAt} />
      ) : null}

      <ListGrid
        kind="mine"
        title={listCopy.home.myLists}
        lists={notCurrent(lists)}
        emptyTitle={listCopy.home.emptyTitle}
        emptyText={listCopy.home.emptyText}
      />

      {membership ? (
        <ListGrid
          kind="class"
          title={listCopy.home.classLists}
          lists={notCurrent(classLists)}
          seenAt={seenAt}
          emptyTitle={listCopy.home.classEmptyTitle}
          emptyText={listCopy.home.classEmptyText}
        />
      ) : null}

      {/* Quiet, and only when there is something in it. */}
      {archived > 0 ? (
        <p>
          <Link to="/lists/archive" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
            {listCopy.home.archiveLink(archived)}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/**
 * One bar for the whole class. It never names anyone: no ranking, no «you
 * added 40», nothing that turns a child's week into a number others compare.
 */
function ClassGoal({ done, target }: { done: number; target: number }) {
  const reached = done >= target;
  const share = Math.min(1, done / Math.max(1, target));
  const doneText = count.format(done);
  const targetText = count.format(target);
  return (
    <Card as="section" className="space-y-3 p-5 sm:p-7">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-xl sm:text-2xl">{listCopy.home.goalTitle}</h2>
        <p className="font-semibold tabular-nums">{listCopy.home.goalCount(doneText, targetText)}</p>
      </div>
      <div
        className="h-3 overflow-hidden rounded-full bg-paper-2"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={target}
        aria-valuenow={Math.min(done, target)}
        aria-label={listCopy.home.goalAria(doneText, targetText)}
      >
        <div
          className="h-full rounded-full bg-fox transition-[width] duration-500"
          style={{ width: `${share * 100}%` }}
        />
      </div>
      {/* Reached is said in words, not only by a full bar (docs/UX.md §8). */}
      <p className={`text-sm ${reached ? "font-semibold text-moss" : "text-ink-soft"}`}>
        {reached ? listCopy.home.goalReached : listCopy.home.goalHint}
      </p>
    </Card>
  );
}
